import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import createPaginationControl from "../../Common/createPaginationControl";
import PrintOut from "../../Common/PrintOut";
import Spinner from "../../Common/Spinner";
import NavigationBarContainer from "../NavigationBarContainer";
import EttersendelserFilterContainer from "./EttersendelserFilterContainer";
import List from "./List";

import { getEttersendelseList } from "../../../selectors/ettersendelserSelector";

import { getEttersendelser as getEttersendelserActions } from "../../../actions/ettersendelserActions";

import { putIleggelseEttersendelserTilGodkjenning as putIleggelseEttersendelserTilGodkjenningAction } from "../../../actions/ettersendelserActions";
import { postIleggelserMakulering as postIleggelserMakuleringAction } from "../../../actions/ileggelseMakuleringerActions";

import sitePaths from "../../../constants/sitePaths";
import * as title from "../../../constants/titles";

import "./EttersendelserPage.css";

const PaginationControl = createPaginationControl(
  "ettersendelser",
  {
    apiCallAction: getEttersendelserActions,
    queriesForApiReducerName: "forms.ettersendelserFilterForm",
  },
  getEttersendelseList,
);

function buildCheckedEttersendelser(items) {
  return {
    ..._.reduce(
      items
        .filter(
          (e) =>
            e?.ettersendelse?.id !== "00000000-0000-0000-0000-000000000000",
        )
        .map((e) => {
          const key = (e.ettersendelse && e.ettersendelse.id) || e.id;
          const value = key === e.id ? undefined : false;
          return { [key]: value };
        }),
      _.extend,
    ),
    checkboxAllEttersendelser: false,
  };
}

export default function EttersendelserPage() {
  const dispatch = useDispatch();
  const ettersendelser = useSelector((state) => state.ettersendelser);
  const ettersendelserFilterForm = useSelector(
    (state) => state.forms.ettersendelserFilterForm,
  );

  const [waitingForGetEttersendelser, setWaitingForGetEttersendelser] =
    useState(false);
  const [checkedEttersendelser, setCheckedEttersendelser] = useState(() =>
    buildCheckedEttersendelser(ettersendelser.items),
  );

  const getEttersendelserFilter = () => {
    const { fraTid, tilTid, status, ...filter } = ettersendelserFilterForm;
    return { ...filter };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    document.title = title.ileggelse.ettersendelser;
    if (
      ettersendelserFilterForm &&
      ettersendelser.page &&
      ettersendelser.count
    ) {
      setWaitingForGetEttersendelser(true);
      dispatch(
        getEttersendelserActions(
          getEttersendelserFilter(),
          ettersendelser.page,
          ettersendelser.count,
        ),
      )
        .then(() => setWaitingForGetEttersendelser(false))
        .catch(() => setWaitingForGetEttersendelser(false));
    }
  }, []);

  // Replaces UNSAFE_componentWillReceiveProps
  useEffect(() => {
    setCheckedEttersendelser(buildCheckedEttersendelser(ettersendelser.items));
  }, [ettersendelser.items]);

  const handleCheckboxChange = (event) => {
    if (event.target.name === "checkboxAllEttersendelser") {
      const keys = Object.keys(checkedEttersendelser);
      const updated = _.reduce(
        keys.map((k) => {
          if (checkedEttersendelser[k] === undefined) {
            return { [k]: undefined };
          }
          return { [k]: event.target.checked };
        }),
        _.extend,
      );
      setCheckedEttersendelser(updated);
    } else {
      setCheckedEttersendelser({
        ...checkedEttersendelser,
        [event.target.name]: event.target.checked,
      });
    }
  };

  const putIleggelseEttersendelserTilGodkjenningBatchJob = () => {
    const keys = Object.keys(checkedEttersendelser);
    const checkedIds = keys.filter(
      (key) =>
        checkedEttersendelser[key] &&
        checkedEttersendelser[key] !== "checkboxAllEttersendelser",
    );
    const tilGodkjenningApiCallArray = checkedIds.map((id) =>
      dispatch(
        putIleggelseEttersendelserTilGodkjenningAction(id, {
          tilGodkjenning: true,
        }),
      ),
    );
    Promise.all(tilGodkjenningApiCallArray)
      .then(() => {
        dispatch(
          getEttersendelserActions(
            getEttersendelserFilter(),
            ettersendelser.page,
            ettersendelser.count,
          ),
        );
      })
      .catch(() => {
        dispatch(
          getEttersendelserActions(
            getEttersendelserFilter(),
            ettersendelser.page,
            ettersendelser.count,
          ),
        );
      });
  };

  const postIleggelserMakulering = (ileggelseId, kommentar) => {
    return dispatch(postIleggelserMakuleringAction(ileggelseId, kommentar))
      .then(() =>
        dispatch(
          getEttersendelserActions(
            getEttersendelserFilter(),
            ettersendelser.page,
            ettersendelser.count,
          ),
        )
          .then(() => Promise.resolve(true))
          .catch(() => Promise.reject(true)),
      )
      .catch(() => Promise.reject(true));
  };

  const showSendTilGodkjenningButton = () => {
    const checkedKeys = Object.keys(checkedEttersendelser).filter(
      (key) => checkedEttersendelser[key],
    );
    const checkedItems = ettersendelser.items.filter((e) => {
      const key = (e.ettersendelse && e.ettersendelse.id) || e.id;
      return checkedKeys.some((ck) => ck === key);
    });
    return (
      checkedItems.length !== 0 &&
      checkedItems.every(
        (e) =>
          e.isTilEttersendelse &&
          !e.isMakulert &&
          !e.isBetalingsfristUtlopt &&
          !e.isBetalt &&
          !e.isVenter &&
          e?.ettersendelse?.id !== "00000000-0000-0000-0000-000000000000",
      )
    );
  };

  return (
    <>
      <NavigationBarContainer page="ettersendelser" />
      <EttersendelserFilterContainer />
      <div style={{ height: 28 }} />
      {(!waitingForGetEttersendelser || ettersendelser.items.length !== 0) && (
        <React.Fragment>
          <div>
            <div className="pull-left" style={{ marginLeft: 50 }}>
              <PrintOut
                url={`${window.location.origin}${sitePaths.ileggelser.ettersendelser.utskrift}`}
                dataToStoreInSessionStorage={{
                  queryData: {
                    ...ettersendelserFilterForm,
                  },
                  paginationData: {
                    page: ettersendelser.page,
                    count: ettersendelser.count,
                  },
                }}
              />
              {showSendTilGodkjenningButton() && (
                <button
                  onClick={putIleggelseEttersendelserTilGodkjenningBatchJob}
                  style={{ marginLeft: 20 }}
                  className="btn-bym-action btn btn-default"
                  type="button"
                >
                  Send til godkjenning
                </button>
              )}
            </div>
            <div
              className="pull-right ettersendelse-list-page-total-ettersendelser"
              style={{ paddingTop: 20, paddingRight: 50 }}
            >
              {ettersendelser.total}{" "}
              {ettersendelser.total === 1 ? "Ettersendelse" : "Ettersendelser"}
            </div>
          </div>
          <div className="clearfix" />
          <div style={{ height: 22 }} />
          <div style={{ paddingRight: 50, paddingLeft: 50 }}>
            <div className="ettersendelse-list-page-content-container">
              <List
                checkedEttersendelser={checkedEttersendelser}
                handleCheckboxChange={handleCheckboxChange}
                ettersendelser={ettersendelser.items}
                postIleggelserMakulering={postIleggelserMakulering}
              />
            </div>
            <PaginationControl storeKey="ettersendelser" />
          </div>
        </React.Fragment>
      )}
      <Spinner
        isLoading={
          waitingForGetEttersendelser && ettersendelser.items.length === 0
        }
      />
      <div style={{ height: 50 }} />
    </>
  );
}
